// services/waitlistService.js
import { getApiUrl } from '../config/api';
import { getCurrentUser } from '../utils/auth';

const currentUser = getCurrentUser();
/**
 * Service class for handling waitlist-related API calls
 */
export class WaitlistService {
  /**
   * Base fetch wrapper with error handling
   */
  static async baseFetch(url, options = {}) {
    try {
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          ...options.headers
        },
        ...options
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Waitlist API Error:', error);
      throw error;
    }
  }

  /**
   * Get all active waitlist entries
   * @returns {Promise<Array>} Array of waitlist entries
   */
  static async getWaitlist() {
    const url = getApiUrl('/api/waitlist');
    return await this.baseFetch(url);
  }

  /**
   * Add a customer to the waitlist
   * @param {object} entry - Waitlist entry data
   * @param {number} entry.CustomerID - Customer ID
   * @param {string} entry.CustomerName - Customer name
   * @param {string} entry.CustomerPhone - Customer phone
   * @param {string} entry.AddedBy - Username who added
   * @returns {Promise<object>} Created entry
   */
  static async addToWaitlist(entry) {
    const url = getApiUrl('/api/waitlist');
    return await this.baseFetch(url, {
      method: 'POST',
      body: JSON.stringify(entry)
    });
  }

  /**
   * Remove a customer from the waitlist
   * @param {number} id - Waitlist entry ID
   * @param {string} currentUser - Username who removed
   * @returns {Promise<object>} Result
   */
  static async removeFromWaitlist(id, currentUser) {
    const url = getApiUrl(`/api/waitlist/${id}?removedBy=${encodeURIComponent(currentUser)}`);
    return await this.baseFetch(url, {
      method: 'DELETE'
    });
  }

  /**
   * Mark a waitlist entry as booked
   * @param {number} id - Waitlist entry ID
   * @param {number} appointmentId - Created appointment ID
   * @param {string} currentUser - Username who booked
   * @returns {Promise<object>} Result
   */
  static async markAsBooked(id, appointmentId, currentUser) {
    const url = getApiUrl(`/api/waitlist/${id}/book`);
    return await this.baseFetch(url, {
      method: 'PUT',
      body: JSON.stringify({
        BookedAppointmentID: appointmentId,
        RemovedBy: currentUser
      })
    });
  }

  /**
   * Scan for open time slots
   * @param {number} stylistId - Stylist MWuserID
   * @param {number} days - Number of days to scan (default 7)
   * @returns {Promise<object>} Scan results with available slots
   */
  static async scanOpenSlots(stylistId, days = 7) {
    const url = getApiUrl(`/api/appointments/open-slots?stylistId=${stylistId}&days=${days}`);
    return await this.baseFetch(url);
  }

  /**
   * Search customers by name or phone
   * @param {string} term - Search term
   * @returns {Promise<Array>} Customer results
   */
  static async searchCustomers(term) {
    if (!term || term.length < 2) return [];
    const url = getApiUrl(`/api/customers/search?term=${encodeURIComponent(term)}`);
    return await this.baseFetch(url);
  }
}

export default WaitlistService;
